// Paradox of choice on the post-tour screen (card 174).
//
// Every button on the thank-you chooser competes with "Leave a review" for the
// same tap. An operator who switches on social, an upsell and four custom links
// has built a menu, and the review button is one row of it. This module derives
// exactly what a customer will end up seeing from the same data the Setup page
// is already editing, so the page can say so before the operator adds the next
// one.
//
// The enablement rules MIRROR the tipping side — TA-Tipping
// app/components/PostTipPage/index.js (`isFeatureEnabledFunctions`) for which
// panes exist, and PostTipPage/Chooser/index.js for which buttons render. An
// absent toggle means enabled there (companies predating the toggle keep their
// behaviour), so `!== false` is the test, not truthiness.
//
// Plan gating is deliberately NOT mirrored. Setup shows the operator every
// section whatever their plan, so counting what is in front of them is what
// makes the number match the page they are reading.

// Four is where the chooser stops being a couple of choices and starts being a
// list to read. Under it we stay quiet; at it we say something.
export const CROWDED_AT = 4;

// Of the five review sites a tour can hold, three or more is the "jam in every
// option" case: the review screen becomes its own menu.
export const REVIEW_LINKS_CROWDED_AT = 3;

export const ChoiceKind = {
  reviews: 'reviews',
  social: 'social',
  upsell: 'upsell',
  customLink: 'customLink'
};

const filled = value => typeof value === 'string' && value.trim() !== '';

// A tour is only served to customers once it has a review link with a URL, so
// that is also the only kind of tour that can put a review button on screen.
// Same rule as ToursSection/constants.js `isTourActive`, restated here to keep
// this module free of component imports (scrollSpy.js sets the precedent).
const hasReviewLink = tour =>
  Boolean(tour && tour.reviewSites && tour.reviewSites.some(site => site && filled(site.url)));

export const countTourReviewLinks = tour =>
  tour && tour.reviewSites ? tour.reviewSites.filter(site => site && filled(site.url)).length : 0;

// Chooser/index.js renders one button per enabled custom link, filtered on
// enabled + buttonText + url. Anything half-filled is invisible to the customer
// and must not be counted against the operator.
export const enabledCustomLinks = links =>
  Array.isArray(links)
    ? links.filter(link => link && link.enabled === true && filled(link.url) && filled(link.buttonText))
    : [];

/**
 * What the customer will see on the post-tour chooser.
 *
 * @param features the company's `postTipPage` feature block
 * @param tours    the company's TipDirect tours (as the Setup page holds them)
 * @returns {{
 *   choices: Array<{ kind: string, label?: string }>,  // in chooser render order
 *   total: number,
 *   crowded: boolean,
 *   skipsChooser: boolean,
 *   reviewsOn: boolean,
 *   busiestTour: { title: string, links: number } | null,
 *   tourLinksCrowded: boolean
 * }}
 */
export function summarisePostTourChoices({ features, tours, tourTitle } = {}) {
  const postTipPage = features || {};
  const tourList = Array.isArray(tours) ? tours : [];
  const choices = [];

  // Reviews: the operator's toggle wins, and there has to be a tour with a
  // review link for the pane to have anywhere to send anyone.
  const reviewsOn = postTipPage.Reviews !== false && tourList.some(hasReviewLink);
  if (reviewsOn) {
    choices.push({ kind: ChoiceKind.reviews });
  }

  // Social: toggle, plus the thank-you text the pane is built around.
  const social = postTipPage.socialLinks;
  if (postTipPage.CustomerSocial !== false && social && filled(social.thankYouText)) {
    choices.push({ kind: ChoiceKind.social });
  }

  // Upsell: toggle, plus both halves of the offer.
  const upsell = postTipPage.upsell;
  if (postTipPage.CustomerUpsell !== false && upsell && filled(upsell.url) && filled(upsell.thankYouText)) {
    choices.push({ kind: ChoiceKind.upsell });
  }

  enabledCustomLinks(postTipPage.externalLinks).forEach(link => {
    choices.push({ kind: ChoiceKind.customLink, label: link.buttonText.trim() });
  });

  // Report the worst offender rather than every tour: one named example tells
  // the operator where to look without turning the advice into a second table.
  const busiestTour = tourList.reduce((worst, tour) => {
    const links = countTourReviewLinks(tour);
    if (!worst || links > worst.links) {
      return { title: (tourTitle ? tourTitle(tour) : tour && tour.title) || '', links };
    }
    return worst;
  }, null);

  return {
    choices,
    total: choices.length,
    crowded: choices.length >= CROWDED_AT,
    // With a single pane enabled PostTipPage selects it outright and the chooser
    // never renders, so the customer lands on it with nothing to decide. Custom
    // links are excluded from that shortcut on the tipping side (they all share
    // one feature entry), so a lone custom link still draws a menu.
    skipsChooser: choices.length === 1 && choices[0].kind !== ChoiceKind.customLink,
    reviewsOn,
    busiestTour,
    tourLinksCrowded: Boolean(
      reviewsOn && busiestTour && busiestTour.links >= REVIEW_LINKS_CROWDED_AT
    )
  };
}
